/*eslint-disable*/
import { useEffect } from 'react';
import { Table, Input } from 'reactstrap';
import { Controller, useFieldArray } from 'react-hook-form';
import Icon from '@mdi/react';
import { mdiTrashCanOutline } from '@mdi/js';
import './FlightInformation.scss';
import { useQuery } from '@tanstack/react-query';
import { CLASS_TYPES } from '../constants';
import {
  fetchTravelAirLine,
  fetchTravelCountry,
  fetchTravelFromCity,
  fetchPickListDetailsAutoComplete,
} from '../../../store/apis/autocompleteService';
import { fetchRiskCountries } from '../../../store/apis/travelService';
import BookingAsyncAutocomplete from '../../BookingAgent/FlightOption/BookingAsyncAutocomplete';
import { DatePicker, TimePicker } from '../../Common';

function FlightInformation({
  isEdit,
  isView = false,
  control,
  errors,
  detailsData,
  setValue,
  getValues,
}) {
  const flightDetails = detailsData?.flightDetails;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'flightInformation',
  });

  const { data: riskCountryList } = useQuery({
    queryKey: ['riskCountryList'],
    queryFn: () =>
      fetchRiskCountries({
        filters: [
          {
            key: 'Param1',
            values: ['Y'],
          },
        ],
      }),
  });

  const removeRow = index => {
    if (fields.length > 1) {
      remove(index);
    } else {
      replace({});
    }
  };

  const handleRiskCheck = (index, toCountryLabel) => {
    const isRisk =
      Array.isArray(riskCountryList?.data) &&
      riskCountryList?.data?.some(
        item => item.Description?.trim() === toCountryLabel?.trim(),
      );
    setValue(`flightInformation.${index}.isRiskCountry`, isRisk ? 'Y' : '');
  };

  useEffect(() => {
    if (
      isEdit &&
      flightDetails &&
      Array.isArray(flightDetails) &&
      flightDetails.length > 0
    ) {
      // Map API fields to form fields
      const mapped = flightDetails.map(item => ({
        traveller: item?.TravelClient?.trim(),
        airline: {
          value: item?.Airline?.trim(),
          label: item?.Airline_Description?.trim(),
        },
        date: item?.TravelDate?.trim()?.split('T')?.[0],
        time: item?.TravelTime?.trim()?.replace(/:00$/, ''),
        toCountry: {
          value: item?.TravelToCountry?.trim(),
          label: item?.TravelToCountry_Description?.trim(),
        },
        toCity: {
          value: item?.TravelTo?.trim(),
          label: item?.TravelTo_Description?.trim(),
        },
        class: item.TravelClass?.trim(),
        fromCity: {
          value: item?.TravelFrom?.trim(),
          label: item?.TravelFrom_Description?.trim(),
        },
        isRiskCountry: item.IsRiskCountry?.trim(),
      }));
      replace(mapped);
    } else if (fields.length === 0) {
      append({});
    }
  }, [flightDetails, replace, append]);

  return (
    <div className="new-travel-request-card p-3 bg-white">
      <div className="d-flex flex-row align-items-center justify-content-between w-100 mb-2">
        <div className="fw-semibold fs-5">Flight Information</div>
        <button
          type="button"
          className="custom-secondary-btn"
          onClick={() => append({})}
          disabled={isView}
        >
          Add New
        </button>
      </div>
      <div className="table-responsive" style={{ overflow: 'visible' }}>
        <div className="ntr-table-container" style={{ overflow: 'visible' }}>
          <Table
            className="options-table align-middle text-nowrap ntr-table"
            id="flight-tr-table"
          >
            <thead>
              <tr>
                <th className="text-center px-2">#</th>
                <th>
                  From City <span className="text-danger-tr">*</span>
                </th>
                <th>
                  To Country <span className="text-danger-tr">*</span>
                </th>
                <th>
                  To City <span className="text-danger-tr">*</span>
                </th>
                <th>
                  Date<span className="text-danger-tr">*</span>
                </th>
                <th>Pref. Time</th>
                <th>Pref. Airline</th>
                <th>Class</th>
                <th>Traveller</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="text-center px-2 py-1">{index + 1}.</td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.fromCity`}
                      render={({ field: infield }) => (
                        <BookingAsyncAutocomplete
                          {...infield}
                          name={`flightInformation.${index}.fromCity`}
                          autoCompleteFn={input =>
                            fetchTravelFromCity({
                              prefixText: input,
                            })
                          }
                          mappedFn={rawData =>
                            rawData.map(item => ({
                              value: item.Code,
                              label: item.Description,
                            }))
                          }
                          value={infield.value} // {formatSelectValue(infield.value, 20)}
                          onChange={infield.onChange}
                          placeholder="From City"
                          isClearable
                          displayOption="bothCustom"
                          inputWidth="100%"
                          width="100%"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 2000 }),
                          }}
                          isDisabled={isView}
                        />
                      )}
                    />
                    {errors.flightInformation?.[index]?.fromCity?.value && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {errors.flightInformation[index].fromCity.value.message}
                      </div>
                    )}
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.toCountry`}
                      render={({ field: infield }) => (
                        <BookingAsyncAutocomplete
                          {...infield}
                          autoCompleteFn={input =>
                            fetchTravelCountry({
                              prefixText: input,
                            })
                          }
                          mappedFn={rawData =>
                            rawData.map(item => ({
                              value: item.Code,
                              label: item.Description,
                            }))
                          }
                          value={infield.value} // {formatSelectValue(infield.value, 20)}
                          onChange={selected => {
                            infield.onChange(selected);
                            const toLabel = selected?.label ?? '';

                            handleRiskCheck(index, toLabel);
                          }}
                          placeholder="To Country"
                          isClearable
                          displayOption="bothCustom"
                          inputWidth="100%"
                          width="100%"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 2000 }),
                          }}
                          isDisabled={isView}
                        />
                      )}
                    />
                    {errors.flightInformation?.[index]?.toCountry?.value && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {
                          errors.flightInformation[index].toCountry.value
                            .message
                        }
                      </div>
                    )}
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.toCity`}
                      render={({ field: infield }) => (
                        <BookingAsyncAutocomplete
                          {...infield}
                          autoCompleteFn={input => {
                            const toCountry = getValues(
                              `flightInformation.${index}.toCountry`,
                            );
                            return fetchPickListDetailsAutoComplete({
                              prefixText: input,
                              type: 'TRAVEL_CITY',
                              param1: toCountry?.value ?? '',
                            });
                          }}
                          mappedFn={rawData =>
                            rawData.map(toCity => ({
                              value: toCity.Code,
                              label: toCity.Description,
                            }))
                          }
                          value={infield.value} // {formatSelectValue(infield.value, 15)}
                          onChange={infield.onChange}
                          placeholder="To City"
                          isClearable
                          displayOption="bothCustom"
                          inputWidth="100%"
                          width="100%"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 2000 }),
                          }}
                          isDisabled={isView}
                        />
                      )}
                    />
                    {errors.flightInformation?.[index]?.toCity?.value && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {errors.flightInformation[index].toCity.value.message}
                      </div>
                    )}
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.date`}
                      render={({ field: element }) => (
                        <>
                          <DatePicker
                            {...element}
                            className="border-0 pe-0"
                            disabled={isView}
                            calendarFocus={false}
                          />
                          {errors.flightInformation?.[index]?.date && (
                            <div
                              className="text-danger-tr text-wrap"
                              style={{ marginLeft: '11px' }}
                            >
                              {errors.flightInformation[index].date.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.time`}
                      render={({ field: element }) => (
                        <TimePicker
                          {...element}
                          interval={1}
                          className="border-0 time-picker-pointer"
                          disabled={isView}
                        />
                      )}
                    />
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.airline`}
                      render={({ field: infield }) => (
                        <BookingAsyncAutocomplete
                          {...infield}
                          autoCompleteFn={input =>
                            fetchTravelAirLine({
                              prefixText: input,
                            })
                          }
                          mappedFn={rawData =>
                            rawData.map(item => ({
                              value: item.Code,
                              label: item.Description,
                            }))
                          }
                          value={infield.value} // {formatSelectValue(infield.value, 15)}
                          onChange={infield.onChange}
                          placeholder="Preferred Airline"
                          isClearable
                          displayOption="bothCustom"
                          inputWidth="100%"
                          menuPortalTarget={document.body}
                          width="100%"
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 2000 }),
                          }}
                          isDisabled={isView}
                        />
                      )}
                    />
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.class`}
                      render={({ field: element }) => (
                        <Input
                          {...element}
                          placeholder="Select"
                          type="select"
                          className="border-0 time-picker-pointer"
                          style={{ width: '100%', border: '2px solid #FFFFF' }}
                          disabled={isView}
                        >
                          {CLASS_TYPES.map(type => (
                            <option key={type.key} value={type.key}>
                              {type.value}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.traveller`}
                      render={({ field: element }) => (
                        <Input
                          {...element}
                          placeholder="Traveller"
                          className="border-0"
                          disabled={isView}
                        />
                      )}
                    />
                  </td>
                  <td hidden>
                    <Controller
                      control={control}
                      name={`flightInformation.${index}.isRiskCountry`}
                      render={({ field: element }) => <Input {...element} />}
                    />
                  </td>
                  <td className="px-3 text-center">
                    {index >= 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeRow(index)}
                        disabled={isView}
                      >
                        <Icon
                          path={mdiTrashCanOutline}
                          size={0.7}
                          color="#D92D20"
                        />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default FlightInformation;
